/**
 * #270 slice 2 — paged, lean boot load of the user-messages queue.
 *
 * Replaces the single unbounded `$limit:-1` full-document fetch with keyset pages
 * (`$lean`/`$cursor`/`$limit`, backend PR #1342) so no response is unbounded and no page carries the
 * ~200 KB `linkPreview.thumbnail`. Two parts:
 *
 *   Tier 1 (send-critical)  pending/in-flight rows due within the send horizon. The redux-only
 *                           Chrome send loop depends on this being COMPLETE.
 *   the rest of the queue   pending rows beyond the horizon, and `failed`. Still loaded at boot in
 *                           this slice (the lists that show them arrive in a later slice), but
 *                           paged and lean like Tier 1.
 *
 * Any paged-mode failure falls back to the legacy unbounded query: a send loop that loads
 * everything the old way beats one that loads nothing.
 */
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import { PLANNER_LOAD_PLAN } from '@common/services/planner-filters';
import { SEND_HORIZON_MS, TIER1_STATUSES } from '@common/services/send-horizon';

export const PAGE_SIZE = 500;
/** Safety bound on one walk (200k rows). Hitting it THROWS, so the caller falls back, never truncates. */
export const MAX_PAGES = 400;
export const HORIZON_TICK_MS = 60 * 60 * 1000;
/** After the tab was hidden, a visible event only re-ticks if the last tick is at least this old. */
const VISIBLE_RETICK_MS = 10 * 60 * 1000;

type Dict = Record<string, unknown>;
export type FindFn = (query: Dict) => Promise<UserMessage[] | undefined>;

const base = (): Dict => ({
    $or: [{ deleted: { $ne: true } }],
    'contactList.0._id': { $exists: true },
});

/** Walk one query to exhaustion in lean keyset pages. Throws on any failed or malformed page. */
export const loadLeanPages = async (find: FindFn, query: Dict): Promise<UserMessage[]> => {
    const out: UserMessage[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_PAGES; page++) {
        const rows = await find({
            ...query,
            $sort: { dueDate: 1 },
            $lean: true,
            $limit: PAGE_SIZE,
            ...(cursor ? { $cursor: cursor } : {}),
        });
        // The find thunk swallows errors and resolves undefined: that is a failed page.
        if (!Array.isArray(rows)) throw new Error('[user-message-paging] page fetch failed');
        out.push(...rows);
        // `$limit` is a MINIMUM on the backend (a tie group is never split), so only a SHORT page is the end.
        if (rows.length < PAGE_SIZE) return out;
        const next = new Date(rows[rows.length - 1].dueDate as unknown as string).toISOString();
        if (next === cursor) throw new Error('[user-message-paging] cursor did not advance');
        cursor = next;
    }
    throw new Error(`[user-message-paging] page cap (${MAX_PAGES}) reached`);
};

const tier1Query = (until: Date, from?: Date): Dict => ({
    ...base(),
    status: { $in: [...TIER1_STATUSES] },
    dueDate: { ...(from ? { $gt: from.toISOString() } : {}), $lte: until.toISOString() },
});

const FAILED_STATUSES = PLANNER_LOAD_PLAN.eagerUnbounded.filter((s) => s === 'failed');

let horizonLoadedUntil: number | null = null;

/** The instant up to which Tier 1 is known loaded, or null before the first successful load. */
export const getHorizonLoadedUntil = (): number | null => horizonLoadedUntil;

/**
 * Paged mode excludes rows without a Date `dueDate` (they have no cursor position), but the legacy
 * load included them and the send loop still wants them. They are a tiny set, so one small plain
 * query (no cursor) fetches them. A non-array result is a failed fetch.
 */
/**
 * Failed rows stay eager until slice 3 (Tier-2 lists, #169). A runaway walk (page cap, bad page) must
 * NOT throw away the Tier-1 result, so on failure this alone falls back to the legacy unbounded failed query.
 */
export const loadFailedRows = async (find: FindFn): Promise<UserMessage[]> => {
    const query = { ...base(), status: { $in: [...FAILED_STATUSES] } };
    try {
        return await loadLeanPages(find, query);
    } catch (err) {
        console.warn('[user-message-paging] failed-rows walk failed, using the unbounded failed query', err);
        const rows = await find({ ...query, $sort: { dueDate: 1 }, $limit: -1 });
        if (!Array.isArray(rows)) throw new Error('[user-message-paging] failed-rows fetch failed');
        return rows;
    }
};

export const loadUndatedRows = async (find: FindFn): Promise<UserMessage[]> => {
    const rows = await find({
        ...base(),
        status: { $in: [...PLANNER_LOAD_PLAN.eagerUnbounded] },
        dueDate: { $exists: false },
        $limit: -1,
    });
    if (!Array.isArray(rows)) throw new Error('[user-message-paging] undated-rows fetch failed');
    return rows;
};

/**
 * Load pending/in-flight + failed rows. Resolves with every row fetched (same contract as the old
 * single `$limit:-1` find) and records the horizon mark on success.
 */
export const loadQueue = async (find: FindFn, now: number = Date.now()): Promise<UserMessage[]> => {
    const until = new Date(now + SEND_HORIZON_MS);
    try {
        const parts = await Promise.all([
            loadLeanPages(find, tier1Query(until)),
            loadLeanPages(find, {
                ...base(),
                status: { $in: [...TIER1_STATUSES] },
                dueDate: { $gt: until.toISOString() },
            }),
            loadFailedRows(find),
            loadUndatedRows(find),
        ]);
        horizonLoadedUntil = until.getTime();
        return parts.flat();
    } catch (err) {
        console.warn('[user-message-paging] paged load failed, falling back to the legacy unbounded load', err);
        const rows = await find({
            ...base(),
            status: { $in: [...PLANNER_LOAD_PLAN.eagerUnbounded] },
            $sort: { dueDate: 1 },
            $limit: -1,
        });
        if (!Array.isArray(rows)) throw new Error('[user-message-paging] legacy load failed');
        horizonLoadedUntil = until.getTime();
        return rows;
    }
};

/**
 * Backstop for a dropped MQTT create/patch: fetch Tier-1 rows due in (mark, now+horizon] and move the
 * mark forward. The mark only advances on success, so a failed tick retries the same window.
 */
export const runHorizonTick = async (find: FindFn, now: number = Date.now()): Promise<boolean> => {
    if (horizonLoadedUntil === null) return false; // boot load has not finished: it will set the mark
    const until = new Date(now + SEND_HORIZON_MS);
    if (until.getTime() <= horizonLoadedUntil) return true;
    try {
        await loadLeanPages(find, tier1Query(until, new Date(horizonLoadedUntil)));
        horizonLoadedUntil = until.getTime();
        return true;
    } catch (err) {
        console.warn('[user-message-paging] horizon tick failed; will retry', err);
        return false;
    }
};

let tickTimer: ReturnType<typeof setInterval> | null = null;
let lastTickAt = 0;

/** Idempotent. Ticks hourly and when a hidden tab becomes visible (sleep/wake). */
export const startHorizonTick = (find: FindFn): void => {
    if (tickTimer !== null) return;
    const tick = () => { lastTickAt = Date.now(); void runHorizonTick(find); };
    tickTimer = setInterval(tick, HORIZON_TICK_MS);
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && Date.now() - lastTickAt >= VISIBLE_RETICK_MS) tick();
        });
    }
};

/** Test seam. */
export const resetPagingStateForTests = (): void => {
    horizonLoadedUntil = null;
    if (tickTimer !== null) clearInterval(tickTimer);
    tickTimer = null;
    lastTickAt = 0;
};
